// components/sales/SalesTranscripts.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Calendar, Loader2, Filter, X } from "lucide-react";
import { TrendUp, ExportCurve } from "iconsax-reactjs";
import api from "../../services/api";

// Outcome badge config
const OUTCOME_CONFIG = {
  SOLD: { label: "Sold", color: "bg-emerald-400/10 text-emerald-400" },
  QUAL: { label: "Qualified", color: "bg-green-400/10 text-green-400" },
  SAT_QUAL: { label: "Sat. Qualified", color: "bg-cyan-400/10 text-cyan-400" },
  NON_QUAL: {
    label: "Non-Qualified",
    color: "bg-orange-400/10 text-orange-400",
  },
  NON_SAT: { label: "Non-Sat.", color: "bg-yellow-400/10 text-yellow-400" },
  NOT_INTERESTED: {
    label: "Not Interested",
    color: "bg-red-400/10 text-red-400",
  },
};

const OutcomeBadge = ({ outcome }) => {
  if (!outcome) return <span className="text-xs text-gray-500">—</span>;
  const cfg = OUTCOME_CONFIG[outcome] || {
    label: outcome,
    color: "bg-gray-400/10 text-gray-400",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}
    >
      {cfg.label}
    </span>
  );
};

const ScorePill = ({ score }) => {
  if (score == null) return <span className="text-xs text-gray-500">—</span>;
  const color =
    score >= 75
      ? "text-green-400"
      : score >= 50
        ? "text-yellow-400"
        : score >= 25
          ? "text-orange-400"
          : "text-red-400";
  return <span className={`font-semibold ${color}`}>{score}</span>;
};

const SalesTranscripts = () => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    status: "all",
    sentiment: null,
    qualificationLevel: null,
    isQualified: null,
    suggestedOutcome: null,
    manualOutcome: null,
    externalUserId: null,
    startDate: null,
    endDate: null,
    search: "",
    page: 1,
    limit: 10,
    sortBy: "callStartTime",
    sortOrder: "DESC",
  });

  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchFilterOptions();
    fetchAnalytics();
  }, []);

  useEffect(() => {
    fetchCalls();
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const response = await api.getSalesCallFilterOptions();
      if (response.success) setFilterOptions(response.options);
    } catch (err) {
      console.error("Failed to fetch filter options", err);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.getSalesCallAnalytics();
      if (response.success) setStats(response.analytics);
    } catch (err) {
      console.error("Failed to fetch analytics", err);
    }
  };

  const fetchCalls = async () => {
    try {
      setLoading(true);
      const cleanFilters = Object.entries(filters).reduce((acc, [k, v]) => {
        if (v !== null && v !== "" && v !== "all") acc[k] = v;
        return acc;
      }, {});
      const response = await api.getSalesCallRecordings(cleanFilters);
      if (response.success) {
        setCalls(response.calls);
        setPagination(response.pagination);
      }
    } catch (err) {
      console.error("Failed to fetch sales calls", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }));
    }
  };
  const handlePageChange = (newPage) => {
    setFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
  };
  const formatDuration = (seconds) => {
    if (!seconds) return "—";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const effectiveOutcome = (call) =>
    call.qualification?.manualOutcome ||
    call.qualification?.suggestedOutcome ||
    null;

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Sales Call Recordings</h1>
          <p className="text-sm text-gray-400 mt-1">
            Field sales visit recordings and analysis
          </p>
        </div>
        <button
          onClick={() => setShowFilters((p) => !p)}
          className="flex items-center gap-2 px-4 py-2 bg-[#13161B] border border-[#22262F] rounded-lg text-sm hover:bg-[#1A1D24] transition-colors"
        >
          <Filter size={15} />
          Filters
          {Object.values(filters).filter(
            (v) =>
              v !== null &&
              v !== "" &&
              v !== "all" &&
              v !== 1 &&
              v !== 10 &&
              v !== "callStartTime" &&
              v !== "DESC",
          ).length > 0 && (
            <span className="ml-1 w-4 h-4 bg-purple-500 rounded-full text-xs flex items-center justify-center">
              {
                Object.values(filters).filter(
                  (v) =>
                    v !== null &&
                    v !== "" &&
                    v !== "all" &&
                    v !== 1 &&
                    v !== 10 &&
                    v !== "callStartTime" &&
                    v !== "DESC",
                ).length
              }
            </span>
          )}
        </button>
      </div>

      {/* Stats Row */}
      {/* {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {[
            { label: "Total Calls", value: stats.totalCalls },
            {
              label: "Qualified",
              value: `${stats.qualificationRate}%`,
              sub: `${stats.qualified} calls`,
            },
            { label: "Avg Score", value: stats.avgQualificationScore },
            { label: "Avg Presentation", value: stats.avgPresentationScore },
            {
              label: "Avg Duration",
              value: formatDuration(stats.averageDuration),
            },
          ].map((s, i) => (
            <div
              key={i}
              className="bg-[#13161B] border border-[#22262F] rounded-xl p-4"
            >
              <div className="text-xs text-gray-400 mb-1">{s.label}</div>
              <div className="text-2xl font-bold text-white">{s.value}</div>
              {s.sub && (
                <div className="text-xs text-gray-500 mt-0.5">{s.sub}</div>
              )}
            </div>
          ))}
        </div>
      )} */}

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-4 mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Search */}
          <input
            type="text"
            placeholder="Search agent, appointment ID, name..."
            defaultValue={filters.search}
            onKeyDown={handleSearch}
            className="col-span-2 md:col-span-4 bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          />

          {/* Agent */}
          <select
            value={filters.externalUserId || ""}
            onChange={(e) =>
              handleFilterChange("externalUserId", e.target.value || null)
            }
            className="bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
          >
            <option value="">All Agents</option>
            {filterOptions?.agents?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>

          {/* Status */}
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            className="bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
          >
            <option value="all">All Statuses</option>
            {filterOptions?.statuses?.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>

          {/* Outcome */}
          <select
            value={filters.suggestedOutcome || ""}
            onChange={(e) =>
              handleFilterChange("suggestedOutcome", e.target.value || null)
            }
            className="bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
          >
            <option value="">All Outcomes</option>
            {filterOptions?.outcomes?.map((o) => (
              <option key={o} value={o}>
                {OUTCOME_CONFIG[o]?.label || o}
              </option>
            ))}
          </select>

          {/* Qualification Level */}
          <select
            value={filters.qualificationLevel || ""}
            onChange={(e) =>
              handleFilterChange("qualificationLevel", e.target.value || null)
            }
            className="bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
          >
            <option value="">All Qual. Levels</option>
            {filterOptions?.qualificationLevels?.map((l) => (
              <option key={l} value={l}>
                {l.charAt(0).toUpperCase() + l.slice(1)}
              </option>
            ))}
          </select>

          {/* Date range */}
          <div className="flex gap-2 col-span-2">
            <input
              type="date"
              value={filters.startDate || ""}
              onChange={(e) =>
                handleFilterChange("startDate", e.target.value || null)
              }
              className="flex-1 bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
            />
            <input
              type="date"
              value={filters.endDate || ""}
              onChange={(e) =>
                handleFilterChange("endDate", e.target.value || null)
              }
              className="flex-1 bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
            />
          </div>

          <button
            onClick={() =>
              setFilters({
                status: "all",
                sentiment: null,
                qualificationLevel: null,
                isQualified: null,
                suggestedOutcome: null,
                manualOutcome: null,
                externalUserId: null,
                startDate: null,
                endDate: null,
                search: "",
                page: 1,
                limit: 10,
                sortBy: "callStartTime",
                sortOrder: "DESC",
              })
            }
            className="flex items-center gap-1 text-sm text-gray-400 hover:text-white"
          >
            <X size={14} /> Clear filters
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-[#13161B] border border-[#22262F] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#22262F]">
              {[
                "Agent",
                "Appointment ID",
                "Date",
                "Duration",
                "Qual. Score",
                "Presentation",
                "Outcome",
                "Status",
              ].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#22262F]">
            {loading ? (
              <tr>
                <td colSpan={8} className="py-16 text-center">
                  <Loader2
                    className="mx-auto animate-spin text-purple-400"
                    size={28}
                  />
                </td>
              </tr>
            ) : calls.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="py-16 text-center text-gray-400 text-sm"
                >
                  No sales calls found.
                </td>
              </tr>
            ) : (
              calls.map((call) => (
                <tr
                  key={call.id}
                  onClick={() => navigate(`/sales-calls/${call.id}`)}
                  className="hover:bg-[#1A1D24] cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-white">
                      {call.externalUserName || call.externalUserId}
                    </div>
                    <div className="text-xs text-gray-500">
                      {call.externalUserId}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {call.externalAppointmentId}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {formatDate(call.callStartTime)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {formatDuration(call.duration)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <ScorePill score={call.qualification?.qualificationScore} />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <ScorePill score={call.qualification?.presentationScore} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <OutcomeBadge outcome={effectiveOutcome(call)} />
                      {call.qualification?.manualOutcome &&
                        call.qualification?.suggestedOutcome &&
                        call.qualification.manualOutcome !==
                          call.qualification.suggestedOutcome && (
                          <span className="text-xs text-gray-500">
                            AI:{" "}
                            {
                              OUTCOME_CONFIG[
                                call.qualification.suggestedOutcome
                              ]?.label
                            }
                          </span>
                        )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                      ${
                        call.status === "completed"
                          ? "bg-green-400/10 text-green-400"
                          : call.status === "failed"
                            ? "bg-red-400/10 text-red-400"
                            : "bg-orange-400/10 text-orange-400"
                      }`}
                    >
                      {call.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {!loading && pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#22262F]">
            <span className="text-sm text-gray-400">
              Page {pagination.page} of {pagination.pages} ({pagination.total}{" "}
              calls)
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="px-3 py-1 text-sm bg-[#0C0E12] border border-gray-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#22262F] transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="px-3 py-1 text-sm bg-[#0C0E12] border border-gray-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#22262F] transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesTranscripts;
